import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Filter, Calendar as CalIcon,
  X, User, FileText, Clock, AlertTriangle, CheckCircle, Building
} from 'lucide-react';
import { Calendar, dateFnsLocalizer, View, Event as RBCEvent } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), getDay, locales });

type CalendarTask = RBCEvent & {
  id: string;
  client?: string;
  clientId?: string;
  type: 'Filing Deadline' | 'Payment' | 'Meeting' | 'Task';
  description?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  assignedTo?: string;
  createdAt: Date;
  updatedAt: Date;
};

interface Client {
  id: string;
  name: string;
  company: string;
}

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


const Container = styled.div`
  width: 100%;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  max-width: 1400px;
  margin: 0 auto 2.5rem auto;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div<{ theme: any }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #973031 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const TitleText = styled.div``;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 2.25rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const CalendarSection = styled.div`
  width: 100%;
  max-width: none;
`;

const CalendarCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  border-radius: 16px;
`;

const CalendarHeader = styled.div<{ theme: any }>`
  padding: 1.5rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.background} 100%);
  border-bottom: 1px solid ${props => props.theme.colors.border}40;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CalendarTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NavButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ViewToggle = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  background: ${props => props.theme.colors.background};
  border-radius: 8px;
  padding: 2px;
  border: 1px solid ${props => props.theme.colors.border};
`;

const ViewButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'active'].includes(prop)
})<{ theme: any; active: boolean }>`
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.border}40;
    color: ${props => props.active ? 'white' : props.theme.colors.text};
  }
`;

const CalendarWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1rem;
  width: 100%;
  
  .rbc-calendar {
    font-family: inherit;
    width: 100%;
  }
  
  .rbc-header {
    padding: 0.75rem 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    color: ${props => props.theme.colors.textSecondary};
    background: ${props => props.theme.colors.background};
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
  
  .rbc-month-view, .rbc-time-view {
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    overflow: hidden;
    width: 100%;
  }
  
  .rbc-month-row {
    width: 100%;
  }
  
  .rbc-row {
    width: 100%;
  }
  
  .rbc-date-cell {
    padding: 0.5rem;
    border-right: 1px solid ${props => props.theme.colors.border};
  }
  
  .rbc-day-bg {
    background: ${props => props.theme.colors.surface};
    border-right: 1px solid ${props => props.theme.colors.border};
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
  
  .rbc-today {
    background: ${props => props.theme.colors.primary}10;
  }
  
  .rbc-event {
    border-radius: 6px;
    border: none;
    padding: 2px 6px;
    font-size: 0.8rem;
    font-weight: 500;
  }
  
  .rbc-month-header {
    width: 100%;
  }
  
  .rbc-header + .rbc-header {
    border-left: 1px solid ${props => props.theme.colors.border};
  }
`;


const LegendItem = styled.div<{ theme: any; color: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
  }
  
  &:before {
    content: '';
    width: 12px;
    height: 12px;
    background: ${props => props.color};
    border-radius: 3px;
    flex-shrink: 0;
  }
`;

const LegendText = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 500;
`;

// Modal Components
const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  padding: 0;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
`;

const FormInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const FormSelect = styled.select.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const FormTextarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ModalActions = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem 2rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const TaskDetail = styled.div`
  margin-bottom: 1.5rem;
`;

const TaskDetailLabel = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const TaskDetailValue = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 500;
`;

const StatusBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'status'].includes(prop)
})<{ theme: any; status: 'pending' | 'in_progress' | 'completed' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: #f59e0b20;
          color: #f59e0b;
        `;
      case 'in_progress':
        return `
          background: #0ea5e920;
          color: #0ea5e9;
        `;
      case 'completed':
        return `
          background: #16a34a20;
          color: #16a34a;
        `;
      default:
        return `
          background: ${props.theme.colors.border};
          color: ${props.theme.colors.textSecondary};
        `;
    }
  }}
`;

const PriorityBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'priority'].includes(prop)
})<{ theme: any; priority: 'low' | 'medium' | 'high' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.priority) {
      case 'high':
        return `
          background: #dc262620;
          color: #dc2626;
        `;
      case 'medium':
        return `
          background: #f59e0b20;
          color: #f59e0b;
        `;
      case 'low':
        return `
          background: #16a34a20;
          color: #16a34a;
        `;
      default:
        return `
          background: ${props.theme.colors.border};
          color: ${props.theme.colors.textSecondary};
        `;
    }
  }}
`;

const CalendarPage: React.FC = () => {
  const { theme } = useTheme();
  usePageTitle('Calendar');

  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState<Date>(new Date());
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [viewTaskModalOpen, setViewTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CalendarTask | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    type: 'Task' as CalendarTask['type'],
    priority: 'medium' as CalendarTask['priority'],
    status: 'pending' as CalendarTask['status'],
    clientId: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false
  });

  // Mock clients data
  const clients: Client[] = useMemo(() => [
    { id: '1', name: 'Alpha Logistics Ltd.', company: 'Alpha Logistics Ltd.' },
    { id: '2', name: 'Crestview Foods', company: 'Crestview Foods' },
    { id: '3', name: 'GreenTech Energy PLC', company: 'GreenTech Energy PLC' },
    { id: '4', name: 'BlueWave Media', company: 'BlueWave Media' },
    { id: '5', name: 'Sunrise Construction', company: 'Sunrise Construction' },
  ], []);

  const [tasks, setTasks] = useState<CalendarTask[]>([
    { 
      id: 'task-1',
      title: 'Q3 VAT Filing - Alpha Logistics', 
      start: new Date(), 
      end: new Date(), 
      allDay: true, 
      client: 'Alpha Logistics Ltd.', 
      clientId: '1',
      type: 'Filing Deadline',
      description: 'Complete quarterly VAT filing for Alpha Logistics including all supporting documentation.',
      priority: 'high',
      status: 'pending',
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 'task-2',
      title: 'PAYE Remittance - Crestview Foods', 
      start: new Date(new Date().setDate(new Date().getDate()+3)), 
      end: new Date(new Date().setDate(new Date().getDate()+3)), 
      allDay: true, 
      client: 'Crestview Foods', 
      clientId: '2',
      type: 'Payment',
      description: 'Process PAYE remittance for Crestview Foods employees.',
      priority: 'medium',
      status: 'pending',
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 'task-3',
      title: 'Tax Planning Meeting - GreenTech', 
      start: new Date(new Date().setHours(14,0)), 
      end: new Date(new Date().setHours(15,0)), 
      client: 'GreenTech Energy PLC', 
      clientId: '3',
      type: 'Meeting',
      description: 'Strategic tax planning session with GreenTech management team.',
      priority: 'medium',
      status: 'in_progress',
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 'task-4',
      title: 'Compliance Review - BlueWave Media', 
      start: new Date(new Date().setDate(new Date().getDate()+1)), 
      end: new Date(new Date().setDate(new Date().getDate()+1)), 
      allDay: true, 
      client: 'BlueWave Media', 
      clientId: '4',
      type: 'Task',
      description: 'Comprehensive compliance review of BlueWave Media tax records.',
      priority: 'low',
      status: 'completed',
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    { 
      id: 'task-5',
      title: 'Monthly Tax Return - Sunrise Construction', 
      start: new Date(new Date().setDate(new Date().getDate()+7)), 
      end: new Date(new Date().setDate(new Date().getDate()+7)), 
      allDay: true, 
      client: 'Sunrise Construction', 
      clientId: '5',
      type: 'Filing Deadline',
      description: 'Prepare and submit monthly tax return for Sunrise Construction.',
      priority: 'high',
      status: 'pending',
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    },
  ]);


  const eventPropGetter = (event: CalendarTask) => {
    let bg = theme.colors.primary;
    if (event.type === 'Filing Deadline') bg = '#e11d48';
    if (event.type === 'Payment') bg = '#0ea5e9';
    if (event.type === 'Meeting') bg = '#16a34a';
    if (event.type === 'Task') bg = '#9333ea';
    return { 
      style: { 
        backgroundColor: `${bg}cc`, 
        borderRadius: '6px', 
        border: 'none', 
        color: 'white',
        fontSize: '0.8rem',
        fontWeight: '500'
      } 
    };
  };

  const handleNewTask = () => {
    setNewTaskModalOpen(true);
  };

  const handleCloseNewTaskModal = () => {
    setNewTaskModalOpen(false);
    setTaskForm({
      title: '',
      description: '',
      type: 'Task',
      priority: 'medium',
      status: 'pending',
      clientId: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      allDay: false
    });
  };

  const handleTaskFormChange = (field: string, value: string | boolean) => {
    setTaskForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskForm.title.trim()) return;

    const startDateTime = taskForm.allDay 
      ? new Date(taskForm.startDate)
      : new Date(`${taskForm.startDate}T${taskForm.startTime}`);
    
    const endDateTime = taskForm.allDay
      ? new Date(taskForm.endDate || taskForm.startDate)
      : new Date(`${taskForm.endDate || taskForm.startDate}T${taskForm.endTime || taskForm.startTime}`);

    const selectedClient = clients.find(c => c.id === taskForm.clientId);

    const newTask: CalendarTask = {
      id: `task-${Date.now()}`,
      title: taskForm.title,
      description: taskForm.description,
      start: startDateTime,
      end: endDateTime,
      allDay: taskForm.allDay,
      type: taskForm.type,
      priority: taskForm.priority,
      status: taskForm.status,
      client: selectedClient?.name,
      clientId: taskForm.clientId || undefined,
      assignedTo: 'Tax Consultant',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setTasks(prev => [...prev, newTask]);
    handleCloseNewTaskModal();
  };

  const handleTaskClick = (task: CalendarTask) => {
    setSelectedTask(task);
    setViewTaskModalOpen(true);
  };

  const handleCloseViewTaskModal = () => {
    setViewTaskModalOpen(false);
    setSelectedTask(null);
  };

  const handleUpdateTaskStatus = (taskId: string, newStatus: CalendarTask['status']) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, status: newStatus, updatedAt: new Date() }
        : task
    ));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(prev => prev ? { ...prev, status: newStatus, updatedAt: new Date() } : null);
    }
  };


  const navigateCalendar = (action: 'PREV' | 'NEXT' | 'TODAY') => {
    const newDate = new Date(date);
    if (action === 'PREV') {
      if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
      else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
      else newDate.setDate(newDate.getDate() - 1);
    } else if (action === 'NEXT') {
      if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
      else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
      else newDate.setDate(newDate.getDate() + 1);
    } else {
      setDate(new Date());
      return;
    }
    setDate(newDate);
  };

  return (
    <Container>
      <HeaderSection>
        <TopRow>
          <TitleGroup>
            <IconWrapper theme={theme}>
              <CalendarIcon size={24} />
            </IconWrapper>
            <TitleText>
              <Title theme={theme}>Tax Calendar</Title>
              <Subtitle theme={theme}>Manage deadlines, meetings, and tax-related tasks</Subtitle>
            </TitleText>
          </TitleGroup>
          <Actions>
            <Button variant="outline">
              <Filter size={16} />
            </Button>
                <Button onClick={handleNewTask}>
                  <Plus size={16} style={{ marginRight: '0.5rem' }} />
                  New Task
                </Button>
          </Actions>
        </TopRow>

        <LegendRow>
          <LegendItem theme={theme} color="#e11d48">
            <LegendText theme={theme}>Filing Deadline</LegendText>
          </LegendItem>
          <LegendItem theme={theme} color="#0ea5e9">
            <LegendText theme={theme}>Payment Due</LegendText>
          </LegendItem>
          <LegendItem theme={theme} color="#16a34a">
            <LegendText theme={theme}>Client Meeting</LegendText>
          </LegendItem>
          <LegendItem theme={theme} color="#9333ea">
            <LegendText theme={theme}>Task/Review</LegendText>
          </LegendItem>
        </LegendRow>
      </HeaderSection>

      <CalendarSection>
        <CalendarCard>
          <CalendarHeader theme={theme}>
            <CalendarTitle theme={theme}>
              <CalIcon size={20} />
              {format(date, 'MMMM yyyy')}
            </CalendarTitle>
            <CalendarControls>
              <NavButton theme={theme} onClick={() => navigateCalendar('TODAY')}>
                Today
              </NavButton>
              <NavButton theme={theme} onClick={() => navigateCalendar('PREV')}>
                <ChevronLeft size={16} />
              </NavButton>
              <NavButton theme={theme} onClick={() => navigateCalendar('NEXT')}>
                <ChevronRight size={16} />
              </NavButton>
              <ViewToggle theme={theme}>
                <ViewButton theme={theme} active={view === 'month'} onClick={() => setView('month')}>
                  Month
                </ViewButton>
                <ViewButton theme={theme} active={view === 'week'} onClick={() => setView('week')}>
                  Week
                </ViewButton>
                <ViewButton theme={theme} active={view === 'day'} onClick={() => setView('day')}>
                  Day
                </ViewButton>
              </ViewToggle>
            </CalendarControls>
          </CalendarHeader>
          
          <CalendarWrapper theme={theme}>
            <Calendar
              localizer={localizer}
              events={tasks}
              startAccessor="start"
              endAccessor="end"
              view={view}
              date={date}
              onView={setView}
              onNavigate={setDate}
              onSelectEvent={handleTaskClick}
              style={{ height: 700, width: '100%' }}
              popup
              eventPropGetter={eventPropGetter}
              components={{ toolbar: () => null }}
            />
          </CalendarWrapper>
        </CalendarCard>
      </CalendarSection>

      {/* New Task Modal */}
      <ModalOverlay theme={theme} isOpen={newTaskModalOpen} onClick={handleCloseNewTaskModal}>
        <ModalContent 
          theme={theme} 
          isOpen={newTaskModalOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader theme={theme}>
            <ModalTitle theme={theme}>
              <Plus size={24} />
              Create New Task
            </ModalTitle>
            <CloseButton theme={theme} onClick={handleCloseNewTaskModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <form id="task-form" onSubmit={handleCreateTask}>
              <FormGroup>
                <FormLabel theme={theme}>Task Title *</FormLabel>
                <FormInput
                  theme={theme}
                  type="text"
                  placeholder="Enter task title"
                  value={taskForm.title}
                  onChange={(e) => handleTaskFormChange('title', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel theme={theme}>Description</FormLabel>
                <FormTextarea
                  theme={theme}
                  placeholder="Enter task description"
                  value={taskForm.description}
                  onChange={(e) => handleTaskFormChange('description', e.target.value)}
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormLabel theme={theme}>Task Type</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={taskForm.type}
                    onChange={(e) => handleTaskFormChange('type', e.target.value)}
                  >
                    <option value="Task">Task</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Filing Deadline">Filing Deadline</option>
                    <option value="Payment">Payment</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel theme={theme}>Priority</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={taskForm.priority}
                    onChange={(e) => handleTaskFormChange('priority', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </FormSelect>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <FormLabel theme={theme}>Status</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={taskForm.status}
                    onChange={(e) => handleTaskFormChange('status', e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel theme={theme}>Client (Optional)</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={taskForm.clientId}
                    onChange={(e) => handleTaskFormChange('clientId', e.target.value)}
                  >
                    <option value="">No client selected</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormLabel theme={theme}>
                  <input
                    type="checkbox"
                    checked={taskForm.allDay}
                    onChange={(e) => handleTaskFormChange('allDay', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  All Day Task
                </FormLabel>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormLabel theme={theme}>Start Date *</FormLabel>
                  <FormInput
                    theme={theme}
                    type="date"
                    value={taskForm.startDate}
                    onChange={(e) => handleTaskFormChange('startDate', e.target.value)}
                    required
                  />
                </FormGroup>

                {!taskForm.allDay && (
                  <FormGroup>
                    <FormLabel theme={theme}>Start Time</FormLabel>
                    <FormInput
                      theme={theme}
                      type="time"
                      value={taskForm.startTime}
                      onChange={(e) => handleTaskFormChange('startTime', e.target.value)}
                    />
                  </FormGroup>
                )}
              </FormRow>

              {!taskForm.allDay && (
                <FormRow>
                  <FormGroup>
                    <FormLabel theme={theme}>End Date</FormLabel>
                    <FormInput
                      theme={theme}
                      type="date"
                      value={taskForm.endDate}
                      onChange={(e) => handleTaskFormChange('endDate', e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel theme={theme}>End Time</FormLabel>
                    <FormInput
                      theme={theme}
                      type="time"
                      value={taskForm.endTime}
                      onChange={(e) => handleTaskFormChange('endTime', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
              )}
            </form>
          </ModalBody>

          <ModalActions theme={theme}>
            <Button variant="outline" onClick={handleCloseNewTaskModal}>
              Cancel
            </Button>
            <button 
              type="submit" 
              form="task-form"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                background: theme.colors.primary,
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Plus size={16} />
              Create Task
            </button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>

      {/* View Task Modal */}
      <ModalOverlay theme={theme} isOpen={viewTaskModalOpen} onClick={handleCloseViewTaskModal}>
        <ModalContent 
          theme={theme} 
          isOpen={viewTaskModalOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          {selectedTask && (
            <>
              <ModalHeader theme={theme}>
                <ModalTitle theme={theme}>
                  <FileText size={24} />
                  Task Details
                </ModalTitle>
                <CloseButton theme={theme} onClick={handleCloseViewTaskModal}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                <TaskDetail>
                  <TaskDetailLabel theme={theme}>Task Title</TaskDetailLabel>
                  <TaskDetailValue theme={theme}>{selectedTask.title}</TaskDetailValue>
                </TaskDetail>

                {selectedTask.description && (
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Description</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>{selectedTask.description}</TaskDetailValue>
                  </TaskDetail>
                )}

                <FormRow>
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Type</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>{selectedTask.type}</TaskDetailValue>
                  </TaskDetail>

                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Priority</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      <PriorityBadge theme={theme} priority={selectedTask.priority}>
                        {selectedTask.priority}
                      </PriorityBadge>
                    </TaskDetailValue>
                  </TaskDetail>
                </FormRow>

                <FormRow>
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Status</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      <StatusBadge theme={theme} status={selectedTask.status}>
                        {selectedTask.status === 'pending' && <Clock size={12} />}
                        {selectedTask.status === 'in_progress' && <AlertTriangle size={12} />}
                        {selectedTask.status === 'completed' && <CheckCircle size={12} />}
                        {selectedTask.status.replace('_', ' ')}
                      </StatusBadge>
                    </TaskDetailValue>
                  </TaskDetail>

                  {selectedTask.client && (
                    <TaskDetail>
                      <TaskDetailLabel theme={theme}>Client</TaskDetailLabel>
                      <TaskDetailValue theme={theme}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Building size={16} />
                          {selectedTask.client}
                        </div>
                      </TaskDetailValue>
                    </TaskDetail>
                  )}
                </FormRow>

                <FormRow>
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Start Date</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      {selectedTask.start ? format(selectedTask.start, 'PPP p') : 'Not set'}
                    </TaskDetailValue>
                  </TaskDetail>

                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>End Date</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      {selectedTask.end ? format(selectedTask.end, 'PPP p') : 'Not set'}
                    </TaskDetailValue>
                  </TaskDetail>
                </FormRow>

                {selectedTask.assignedTo && (
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Assigned To</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <User size={16} />
                        {selectedTask.assignedTo}
                      </div>
                    </TaskDetailValue>
                  </TaskDetail>
                )}

                <FormRow>
                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Created</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      {format(selectedTask.createdAt, 'PPP p')}
                    </TaskDetailValue>
                  </TaskDetail>

                  <TaskDetail>
                    <TaskDetailLabel theme={theme}>Last Updated</TaskDetailLabel>
                    <TaskDetailValue theme={theme}>
                      {format(selectedTask.updatedAt, 'PPP p')}
                    </TaskDetailValue>
                  </TaskDetail>
                </FormRow>
              </ModalBody>

              <ModalActions theme={theme}>
                <Button variant="outline" onClick={handleCloseViewTaskModal}>
                  Close
                </Button>
                {selectedTask.status !== 'completed' && (
                  <>
                    {selectedTask.status === 'pending' && (
                      <Button 
                        onClick={() => handleUpdateTaskStatus(selectedTask.id, 'in_progress')}
                      >
                        <AlertTriangle size={16} style={{ marginRight: '0.5rem' }} />
                        Start Task
                      </Button>
                    )}
                    {selectedTask.status === 'in_progress' && (
                      <Button 
                        onClick={() => handleUpdateTaskStatus(selectedTask.id, 'completed')}
                      >
                        <CheckCircle size={16} style={{ marginRight: '0.5rem' }} />
                        Complete Task
                      </Button>
                    )}
                  </>
                )}
              </ModalActions>
            </>
          )}
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
};

export default CalendarPage;


